import React, { useState, useEffect } from "react";
import { concorrentes, produtosConcorrencia, produtos } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate } from "../../lib/utils";

export function ConcorrentesPage() {
  const [concorrentesList, setConcorrentesList] = useState([]);
  const [precosList, setPrecosList] = useState([]);
  const [produtosList, setProdutosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPrecoDialogOpen, setIsPrecoDialogOpen] = useState(false);
  const [selectedConcorrente, setSelectedConcorrente] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    nome: "",
    site: "",
    observacoes: "",
  });

  const [precoFormData, setPrecoFormData] = useState({
    produto_id: "",
    concorrente_id: "",
    valor_unitario_concorrente: 0,
    data_referencia: new Date().toISOString().split("T")[0],
    observacoes: "",
  });

  const fetchData = async () => {
    try {
      const [concRes, precosRes, produtosRes] = await Promise.all([
        concorrentes.list(),
        produtosConcorrencia.list(),
        produtos.list(),
      ]);
      setConcorrentesList(concRes.data);
      setPrecosList(precosRes.data);
      setProdutosList(produtosRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await concorrentes.create(formData);
      toast.success("Concorrente criado!");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar");
    }
  };

  const handlePrecoSubmit = async (e) => {
    e.preventDefault();
    try {
      await produtosConcorrencia.create(precoFormData);
      toast.success("Preço registrado!");
      setIsPrecoDialogOpen(false);
      resetPrecoForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este concorrente?")) return;
    try {
      await concorrentes.delete(id);
      toast.success("Concorrente excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      site: "",
      observacoes: "",
    });
  };

  const resetPrecoForm = () => {
    setPrecoFormData({
      produto_id: "",
      concorrente_id: "",
      valor_unitario_concorrente: 0,
      data_referencia: new Date().toISOString().split("T")[0],
      observacoes: "",
    });
  };

  const filteredConcorrentes = concorrentesList.filter((item) =>
    item.nome?.toLowerCase().includes(search.toLowerCase())
  );

  const getPrecosByConcorrente = (concorrenteId) => {
    return precosList
      .filter((p) => p.concorrente_id === concorrenteId)
      .map((p) => {
        const produto = produtosList.find((prod) => prod.id === p.produto_id);
        return {
          ...p,
          produto_descricao: produto?.descricao || "Produto não encontrado",
        };
      });
  };

  const columns = [
    { key: "nome", header: "Nome" },
    { key: "site", header: "Site" },
    { key: "observacoes", header: "Observações" },
    { key: "status", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setSelectedConcorrente(row)} data-testid={`view-concorrente-${row.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin() && (
            <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const precoColumns = [
    { key: "produto_descricao", header: "Produto" },
    { key: "valor_unitario_concorrente", header: "Valor", render: (v) => formatCurrency(v) },
    { key: "data_referencia", header: "Data Ref.", render: (v) => formatDate(v) },
    { key: "observacoes", header: "Observações" },
  ];

  return (
    <div className="space-y-6" data-testid="concorrentes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Concorrentes</h1>
          <p className="text-muted-foreground">Monitore os preços da concorrência</p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && (
            <>
              <Dialog open={isPrecoDialogOpen} onOpenChange={(open) => { setIsPrecoDialogOpen(open); if (!open) resetPrecoForm(); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-preco-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Preço
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Preço Concorrente</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handlePrecoSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Produto *</Label>
                      <Select value={precoFormData.produto_id} onValueChange={(v) => setPrecoFormData({ ...precoFormData, produto_id: v })}>
                        <SelectTrigger data-testid="preco-produto">
                          <SelectValue placeholder="Selecione o produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {produtosList.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Concorrente *</Label>
                      <Select value={precoFormData.concorrente_id} onValueChange={(v) => setPrecoFormData({ ...precoFormData, concorrente_id: v })}>
                        <SelectTrigger data-testid="preco-concorrente">
                          <SelectValue placeholder="Selecione o concorrente" />
                        </SelectTrigger>
                        <SelectContent>
                          {concorrentesList.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Valor Unitário *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={precoFormData.valor_unitario_concorrente}
                          onChange={(e) => setPrecoFormData({ ...precoFormData, valor_unitario_concorrente: parseFloat(e.target.value) || 0 })}
                          required
                          data-testid="preco-valor"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Referência</Label>
                        <Input
                          type="date"
                          value={precoFormData.data_referencia}
                          onChange={(e) => setPrecoFormData({ ...precoFormData, data_referencia: e.target.value })}
                          data-testid="preco-data"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Observações</Label>
                      <Input
                        value={precoFormData.observacoes}
                        onChange={(e) => setPrecoFormData({ ...precoFormData, observacoes: e.target.value })}
                        data-testid="preco-obs"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsPrecoDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" data-testid="save-preco-btn">Salvar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-concorrente-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Concorrente
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Concorrente</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome *</Label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="concorrente-nome"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Site</Label>
                      <Input
                        value={formData.site}
                        onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                        data-testid="concorrente-site"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Observações</Label>
                      <Input
                        value={formData.observacoes}
                        onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                        data-testid="concorrente-obs"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" data-testid="save-concorrente-btn">Criar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar concorrente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-concorrentes"
          />
        </div>
      </div>

      <DataTable columns={columns} data={filteredConcorrentes} loading={loading} emptyMessage="Nenhum concorrente cadastrado" />

      {/* View Precos Dialog */}
      <Dialog open={!!selectedConcorrente} onOpenChange={() => setSelectedConcorrente(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preços - {selectedConcorrente?.nome}</DialogTitle>
          </DialogHeader>
          {selectedConcorrente && (
            <div className="space-y-4">
              {selectedConcorrente.site && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Site:</span>{" "}
                  <a href={selectedConcorrente.site} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                    {selectedConcorrente.site}
                  </a>
                </p>
              )}
              <DataTable
                columns={precoColumns}
                data={getPrecosByConcorrente(selectedConcorrente.id)}
                loading={false}
                emptyMessage="Nenhum preço registrado para este concorrente"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
